import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Modal } from "antd";
import { Alert } from "components/elements";

import useOptions from "features/masterData/hooks/useOptions";
import * as services from "../services/leaveTypeApi";
import { setIsLoading } from "../slices/attendanceSlice";
import useLeaveType from "./useLeaveType";
import useLeaveTypeEdit from "./useLeaveTypeEdit";

function toParams(values = {}) {
  const modifiedValues = {
    ...values,
    emergencyLeave: values.emergencyLeave ? "Y" : "N",
    halfDayLeave: values.halfDayLeave ? "Y" : "N",
    consecutiveLimit: values.consecutiveLimit ? "Y" : "N",
    permanentOnly: values.permanentOnly ? "Y" : "N",
    allowOnce: values.allowOnce ? "Y" : "N",
    countHolidays: values.countHolidays ? "Y" : "N",
    extraAllowance: values.extraAllowance ? "Y" : "N",
    diligenceAllowance: values.diligenceAllowance ? "Y" : "N",
    leaveWithoutPay: values.leaveWithoutPay ? "Y" : "N",
    includeProbation: values.includeProbation ? "Y" : "N",
    avalibleInBirth: values.avalibleInBirth ? "Y" : "N",
    attachment: values.attachment ? "Y" : "N",
    accumulatedAnnualLeave: values.accumulatedAnnualLeave ? "Y" : "N",
  };
  return modifiedValues;
}

function useLeaveTypeCreate() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const currentCompany = useSelector((state) => state.company.currentCompany);
  const isLoading = useSelector((state) => state.attendance.isLoading);
  const { leaveGroupOptions } = useLeaveType()
  const { modifiedWorkInLocationOption, getDropdownWorkInLocationForOffsite } = useLeaveTypeEdit();

  const {
    // leaveGroupOptions,
    leaveRequestConditionOptions,
    monthWeekDayWorkingOptions,
    genderOptions,
    leaveCalculationOptions,
    minimumLeaveGroupOptions,
    documentLimitGroupOptions,
    getLeaveGroupOptions,
    getLeaveRequestConditionOptions,
    getMonthWeekDayWorkingOptions,
    getGenderOptions,
    getLeaveCalculationOptions,
    getMinimumLeaveGroupOptions,
    getDocumentLimitGroupOptions,
  } = useOptions();

  // const { leaveGroupOptions,
  //   getLeaveCategoryOption } = useLeaveType();

  const onCancel = useCallback(() => {
    navigate("/configuration/attendance", { state: { selectedTab: 'leave' } });
  }, [navigate]);

  const createLeaveTypeCompany = useCallback(
    async (values) => {
      try {
        dispatch(setIsLoading(true));
        const result = await services.createLeaveTypeCompany(
          toParams({ ...values, companyId: currentCompany })
        );

        const content = result?.data?.content;
        const isSuccess = content?.code === "200";

        if (!isSuccess) {
          Modal.error({
            content: <span>{t('duplicate_in_the_system')}</span>,
            okText: t('message.ok'),
          });
          return;
        }

        Alert({ type: "success", message: "Success" });
        onCancel();
      } catch (error) {
        Alert({ type: "error", resultObject: error, noti: true });
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, dispatch, onCancel, t]
  );

  const onSubmit = useCallback(
    (values) => {
      Modal.confirm({
        title: t('translation.confirmation'),
        content: (
          <>
            <p>{t('translation.are-you-sure-confirm-to-create')}</p>
          </>
        ),
        onOk() {
          createLeaveTypeCompany(values);
        },
        okText: t('translation.confirm-0'),
        cancelText: t('translation.action.cancel'),
      });
    },
    [createLeaveTypeCompany]
  );

  // useEffect(() => {
  //   getLeaveCategoryOption();
  // }, []);

  useEffect(() => {
    getLeaveRequestConditionOptions();
  }, [getLeaveRequestConditionOptions]);

  useEffect(() => {
    getMonthWeekDayWorkingOptions();
  }, [getMonthWeekDayWorkingOptions]);

  useEffect(() => {
    getGenderOptions();
  }, [getGenderOptions]);

  useEffect(() => {
    getLeaveCalculationOptions();
  }, [getLeaveCalculationOptions]);

  useEffect(() => {
    getMinimumLeaveGroupOptions();
  }, [getMinimumLeaveGroupOptions]);

  useEffect(() => {
    getDocumentLimitGroupOptions();
  }, [getDocumentLimitGroupOptions]);


  return {
    isLoading,
    leaveGroupOptions,
    leaveRequestConditionOptions,
    monthWeekDayWorkingOptions,
    genderOptions,
    leaveCalculationOptions,
    minimumLeaveGroupOptions,
    documentLimitGroupOptions,
    onCancel,
    onSubmit,
    modifiedWorkInLocationOption
  };
}

export default useLeaveTypeCreate;
